import {
  encodeEnvelope,
  encodeJson,
  type Envelope,
} from '@/lib/sync/envelope';
import {
  KIND_AGENT_TOOL,
  KIND_AGENT_EVENT,
  KIND_AGENT_DONE,
} from '@/lib/sync/envelope-kinds';
import type { SyncTransport } from '@/lib/sync/sync-transport';
import type { EditRequest } from './types';

function corrIdMatches(corrIdBytes: Uint8Array, runIdHex: string): boolean {
  if (corrIdBytes.length !== 16) return false;
  for (let i = 0; i < 16; i++) {
    const high = parseInt(runIdHex.charAt(i * 2), 16);
    const low = parseInt(runIdHex.charAt(i * 2 + 1), 16);
    if (((high << 4) | low) !== corrIdBytes[i]) return false;
  }
  return true;
}

/**
 * Send a KIND_AGENT_TOOL frame over syncTransport and return a
 * ReadableStream<Uint8Array> of raw response chunk JSON bytes.
 *
 * This is a pure protocol helper over SyncTransport — it does not depend on
 * any chat or conversation state and may be used from any context that has
 * access to the shared transport (e.g. EditActionTransportProvider in the
 * router).
 */
export async function openEditActionStream(
  syncTransport: SyncTransport,
  runId: string,
  req: EditRequest,
  blockContent: string,
): Promise<ReadableStream<Uint8Array>> {
  const body = {
    action: req.action,
    entityKind: req.entityKind,
    entityId: req.entityId,
    target: req.target,
    params: req.params ?? {},
    blockContent,
  };
  const frame = encodeEnvelope({
    kind: KIND_AGENT_TOOL,
    corrId: runId,
    json: encodeJson(body),
  });

  const ok = syncTransport.send(frame);
  if (!ok) {
    throw new Error('not_connected');
  }

  const runIdHex = runId.replace(/-/g, '');

  let unsubscribe: (() => void) | undefined;
  return new ReadableStream<Uint8Array>({
    start: (controller) => {
      unsubscribe = syncTransport.onFrame((envelope: Envelope) => {
        if (!corrIdMatches(envelope.corrId, runIdHex)) return;

        if (envelope.kind === KIND_AGENT_EVENT) {
          if (envelope.json.length > 0) {
            controller.enqueue(envelope.json);
          }
          return;
        }

        if (envelope.kind === KIND_AGENT_DONE) {
          unsubscribe?.();
          unsubscribe = undefined;
          try { controller.close(); } catch { /* already closed */ }
        }
      });
    },
    cancel: () => {
      unsubscribe?.();
      unsubscribe = undefined;
    },
  });
}
